import { useCallback, useMemo } from 'react';
import { newId } from '@battleops/localstore';
import { commands, buildGoalNotice, valuesFor, formatMetric, periodTotals } from '@battleops/shared';
import { useLocalCollection } from './useLocal';
import { commitWrites } from './commit';
import { showToast } from './toast';
import { useSession } from '../context/SessionContext';
import { useTasks } from '../context/TaskContext';
import { useProjects } from '../context/ProjectContext';

// Metrics (CFO plan 4.4). Business: `metrics` (opsMetrics — owners define, everyone reads) + `metricValues`
// (opsMetricValues — yours, or everyone's for an owner). Personal: `personalMetrics` + `personalMetricValues`
// (opsUsers/{uid}/…) — private, never in team or CFO views. Every write is a shared command, one group.

/** { business, personal, values, personalValues, save, remove, log, undo, capture }. */
export function useMetrics() {
  const business = useLocalCollection('metrics');
  const personal = useLocalCollection('personalMetrics');
  const values = useLocalCollection('metricValues');
  const personalValues = useLocalCollection('personalMetricValues');
  const { identity } = useSession() || {};
  const uid = identity?.uid || null;
  const sorted = useCallback((list) => [...list].sort((a, b) => String(a.name).localeCompare(String(b.name))), []);
  const biz = useMemo(() => sorted(business), [business, sorted]);
  const mine = useMemo(() => sorted(personal), [personal, sorted]);
  const ctx = useCallback(() => ({ newId, now: Date.now(), uid, metrics: business, personalMetrics: personal, metricValues: values, personalMetricValues: personalValues }),
    [uid, business, personal, values, personalValues]);
  const run = useCallback((r) => { commitWrites(r.writes).catch((e) => console.error('metrics', e)); return r; }, []);

  const save = useCallback((metric, personalDef = false) => run(commands.saveMetric(ctx(), { metric, personal: personalDef })).metric, [ctx, run]);
  const remove = useCallback((id, personalDef = false) => { run(commands.deleteMetric(ctx(), { id, personal: personalDef })); }, [ctx, run]);
  const undo = useCallback((id, personalDef = false) => { run(commands.deleteMetricValue({ ...ctx(), [personalDef ? 'personalMetricValues' : 'metricValues']: [{ id }] }, { id, personal: personalDef })); }, [ctx, run]);
  /**
   * Log one value ({ metricId, value, personal, note, taskId, projectId, source }); shows the undo toast (`undoable`)
   * and the goal notice when the goal was just crossed. Returns the value doc.
   */
  const log = useCallback((input, { undoable = true } = {}) => {
    const c = ctx();
    const r = run(commands.logMetric(c, input));
    const def = (input.personal ? c.personalMetrics : c.metrics).find((d) => d.id === input.metricId);
    if (undoable) showToast({ title: `${def?.name || 'Logged'} ${input.value == null || input.value === 1 ? '+1' : formatMetric(def, r.value.value)}`, action: { label: 'Undo', run: () => undo(r.value.id, !!input.personal) } });
    const rows = (input.personal ? c.personalMetricValues : c.metricValues).filter((v) => v.metricId === input.metricId && (input.personal || v.uid === r.value.uid));
    const total = def?.goal ? periodTotals(def, [...rows, r.value])[def.goal.period] : null;
    const notice = buildGoalNotice(def, r.crossed, { total });
    if (notice) showToast({ ...notice, ms: 8000 });
    return r.value;
  }, [ctx, run, undo]);
  /** The values asked for when a task was done (the completion sheet): [{ metricId, value }]. */
  const capture = useCallback((task, captures) => run(commands.captureWrites(ctx(), { task, captures })).values, [ctx, run]);
  return { business: biz, personal: mine, values, personalValues, uid, save, remove, log, undo, capture };
}

/** What auto metrics are computed from: sessions, tasks, projects, the books' receipts and jobs, routine runs. */
export function useMetricCtx() {
  const { tasks, allClockSessions, clockSessions } = useTasks();
  const { projects } = useProjects();
  const receipts = useLocalCollection('fieldReceipts');
  const jobs = useLocalCollection('jobs');
  const runs = useLocalCollection('processExecutions');
  const { identity } = useSession() || {};
  return useMemo(() => ({ tasks, projects, sessions: allClockSessions || clockSessions || [], receipts, jobs, runs, me: identity?.uid || null, now: Date.now() }),
    [tasks, projects, allClockSessions, clockSessions, receipts, jobs, runs, identity?.uid]);
}

/** A def's rows (auto computed, else stored). Personal: your personal values, and auto rows about you only. */
export function rowsFor(def, { values, personalValues, ctx, personal = false }) {
  const rows = valuesFor(def, { values: personal ? personalValues : values, ctx });
  return personal ? rows.filter((r) => !r.uid || r.uid === ctx?.me) : rows;
}
